import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { MapContainer, Marker, Polyline, TileLayer, useMapEvents } from "react-leaflet";
import MapZoomLocateControl from "../map/MapZoomLocateControl";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { useNotification } from "../../notifications/NotificationContext";
import FormRequiredMark from "../FormRequiredMark";
import PhotoUploadPreview from "../PhotoUploadPreview";
import { PHOTO_UPLOAD_ACCEPT, validatePhotoSelection } from "../../utils/uploadValidation";
import { useDepthPointsLoader } from "../../hooks/useDepthPointsLoader";
import spotGreenIcon from "../../assets/icons/map-icon.svg";
import WizardStepTabs from "./WizardStepTabs";
import WizardNavButtons from "./WizardNavButtons";
import { buildSpotFormData } from "./spotFormPayload";
import {
    BANK_TYPE_OPTIONS,
    CURRENT_STRENGTH_OPTIONS,
    NEW_SPOT_DRAFT_STORAGE_KEY,
    ROAD_TYPE_OPTIONS,
    STEP_TITLES_BY_MODE,
} from "./spotFormConstants";
import { CREATE_INITIAL_FORM_STATE, buildFormStateFromSpot } from "./spotFormState";
import { clamp, fieldError, validateBeforeSubmit, validateStep } from "./spotFormValidation";
import SonarDepthLayer, { MapDepthViewportListener } from "./map/SonarDepthLayer";
import DrawMapInteraction from "./map/DrawMapInteraction";
import InvalidateMapSizeOnMount from "./map/InvalidateMapSizeOnMount";
import {toDateTimeLocalValue} from "../../utils/spotDetailUtils";
import {fishingTypeSelectOptions} from "../../utils/fishLabelsRu";
import OptionMultiSelect from "../OptionMultiSelect";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";
const spotIcon = L.icon({ iconUrl: spotGreenIcon, iconSize: [60, 40], iconAnchor: [30, 40] });

function scrollPageToTop() {
    requestAnimationFrame(() => {
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
    });
}

function showFormFeedback(setMessage, setMessageType, text, type = "error") {
    setMessage(text);
    setMessageType(type);
    if (type === "error") {
        scrollPageToTop();
    }
}

function normalizePhotoSrc(photo) {
    if (!photo) return "";
    const raw = typeof photo === "string" ? photo : photo.path;
    if (!raw) return "";
    if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
    return `${API_BASE_URL}${raw}`;
}

function routeToLine(route) {
    const coordinates = route?.geometry?.coordinates;
    if (!Array.isArray(coordinates)) return [];
    return coordinates
        .filter((point) => Array.isArray(point) && point.length >= 2)
        .map((point) => [Number(point[1]), Number(point[0])])
        .filter((point) => Number.isFinite(point[0]) && Number.isFinite(point[1]));
}

export default function SpotFormWizard({
    mode,
    spotId,
    initialSpot,
    onSubmitSuccess,
}) {
    const isCreate = mode === "create";
    const stepTitles = STEP_TITLES_BY_MODE[mode];
    const { showNotification } = useNotification();

    const [form, setForm] = useState(() => {
        if (isCreate) {
            return CREATE_INITIAL_FORM_STATE;
        }
        return initialSpot ? buildFormStateFromSpot(initialSpot) : null;
    });
    const [placeInfo, setPlaceInfo] = useState(null);
    const [detectingPlace, setDetectingPlace] = useState(false);
    const [allFish, setAllFish] = useState([]);
    const [files, setFiles] = useState({ place: [], catch: [], depth: [] });
    const { depthPoints, loadDepthPoints } = useDepthPointsLoader(isCreate);
    const [selectedFishIds, setSelectedFishIds] = useState([]);
    const [existingRoutes, setExistingRoutes] = useState([]);
    const [message, setMessage] = useState("");
    const [messageType, setMessageType] = useState("error");
    const [saving, setSaving] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const [fileErrors, setFileErrors] = useState({});
    const [routeError, setRouteError] = useState("");
    const [drawMode, setDrawMode] = useState("move");
    const [drawPoints, setDrawPoints] = useState([]);
    const [, setDrawSegments] = useState([]);
    const [routeDescription, setRouteDescription] = useState("");
    const [currentStep, setCurrentStep] = useState(0);
    const [maxReachedStep, setMaxReachedStep] = useState(0);
    const [fishSearch, setFishSearch] = useState("");
    const [previewPhoto, setPreviewPhoto] = useState(null);
    const [reportFormOpen, setReportFormOpen] = useState(false);
    const [reportFiles, setReportFiles] = useState({ place: [], catch: [], depth: [] });
    const [reportFileErrors, setReportFileErrors] = useState({});
    const skipStepScrollRef = useRef(true);

    const spot = isCreate ? null : initialSpot;
    const lat = isCreate ? Number(form?.latitude) : Number(spot?.latitude);
    const lng = isCreate ? Number(form?.longitude) : Number(spot?.longitude);
    const stepTitle = stepTitles[currentStep];

    const setField = useCallback((name, value) => {
        setForm((prev) => (prev ? { ...prev, [name]: value } : prev));
        setFieldErrors((prev) => {
            if (!prev[name]) return prev;
            const { [name]: _removed, ...rest } = prev;
            return rest;
        });
    }, []);

    const inputClass = (name, baseClass = "ui-input") => `${baseClass}${fieldErrors[name] ? " is-invalid" : ""}`;
    const setShoreFishing = (enabled) => {
        setForm((prev) => (prev ? { ...prev, fishingFromShore: enabled, fishingFromBoat: !enabled } : prev));
    };

    useEffect(() => {
        if (!isCreate && initialSpot) {
            setForm(buildFormStateFromSpot(initialSpot));
        }
    }, [isCreate, initialSpot]);

    useEffect(() => {
        if (!isCreate && spotId) {
            axios.get(`${API_BASE_URL}/api/spots/${spotId}/routes`)
                .then((res) => setExistingRoutes(Array.isArray(res.data) ? res.data : []))
                .catch(() => setExistingRoutes([]));
        }
    }, [isCreate, spotId]);

    useEffect(() => {
        axios.get(`${API_BASE_URL}/api/fish`)
            .then((res) => setAllFish(res.data || []))
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (skipStepScrollRef.current) {
            skipStepScrollRef.current = false;
            return;
        }
        scrollPageToTop();
    }, [currentStep]);

    useEffect(() => {
        if (!isCreate) return undefined;
        try {
            const raw = window.localStorage.getItem(NEW_SPOT_DRAFT_STORAGE_KEY);
            if (!raw) return;
            const parsed = JSON.parse(raw);
            if (parsed?.form && typeof parsed.form === "object") {
                setForm((prev) => ({ ...prev, ...parsed.form }));
            }
            if (parsed?.placeInfo) {
                setPlaceInfo(parsed.placeInfo);
            }
            if (Array.isArray(parsed?.selectedFishIds)) {
                setSelectedFishIds(parsed.selectedFishIds.map((id) => Number(id)).filter(Number.isFinite));
            }
            if (Array.isArray(parsed?.drawPoints)) {
                setDrawPoints(parsed.drawPoints);
            }
            if (typeof parsed?.routeDescription === "string") {
                setRouteDescription(parsed.routeDescription);
            }
            if (Number.isInteger(parsed?.currentStep)) {
                setCurrentStep(clamp(parsed.currentStep, 0, stepTitles.length - 1));
            }
            if (Number.isInteger(parsed?.maxReachedStep)) {
                setMaxReachedStep(clamp(parsed.maxReachedStep, 0, stepTitles.length - 1));
            }
        } catch {
            // ignore corrupted drafts
        }
    }, [isCreate, stepTitles.length]);

    useEffect(() => {
        if (!isCreate) return;
        const payload = {
            form,
            placeInfo,
            selectedFishIds,
            drawPoints,
            routeDescription,
            currentStep,
            maxReachedStep,
        };
        window.localStorage.setItem(NEW_SPOT_DRAFT_STORAGE_KEY, JSON.stringify(payload));
    }, [isCreate, form, placeInfo, selectedFishIds, drawPoints, routeDescription, currentStep, maxReachedStep]);

    useEffect(() => {
        if (!previewPhoto) return undefined;
        const onEsc = (event) => {
            if (event.key === "Escape") {
                setPreviewPhoto(null);
            }
        };
        window.addEventListener("keydown", onEsc);
        return () => window.removeEventListener("keydown", onEsc);
    }, [previewPhoto]);

    const orderedFish = useMemo(() => {
        const fishList = Array.isArray(allFish) ? allFish : [];
        const search = fishSearch.trim().toLowerCase();

        if (isCreate) {
            const inPlaceIds = new Set(((placeInfo?.fish || []).map((f) => Number(f.id))).filter(Number.isFinite));
            return [...fishList]
                .filter((fish) => !search || String(fish?.name || "").toLowerCase().includes(search))
                .sort((a, b) => {
                    const aInPlace = inPlaceIds.has(Number(a?.id)) ? 1 : 0;
                    const bInPlace = inPlaceIds.has(Number(b?.id)) ? 1 : 0;
                    if (aInPlace !== bInPlace) return bInPlace - aInPlace;
                    return String(a?.name || "").localeCompare(String(b?.name || ""), "ru");
                });
        }

        const selected = form?.fishIds || new Set();
        return [...fishList]
            .filter((fish) => !search || String(fish?.name || "").toLowerCase().includes(search))
            .sort((a, b) => {
                const aSelected = selected.has(Number(a?.id)) ? 1 : 0;
                const bSelected = selected.has(Number(b?.id)) ? 1 : 0;
                if (aSelected !== bSelected) return bSelected - aSelected;
                return String(a?.name || "").localeCompare(String(b?.name || ""), "ru");
            });
    }, [allFish, fishSearch, form?.fishIds, isCreate, placeInfo]);

    const handleReportPhotoChange = (type, fileList) => {
        const result = validatePhotoSelection(type, fileList);
        setReportFiles((prev) => ({ ...prev, [type]: result.files }));
        setReportFileErrors((prev) => ({ ...prev, [type]: result.error }));
    };

    const detectPlace = useCallback(async (latArg, lngArg) => {
        const latVal = latArg ?? Number(form?.latitude);
        const lngVal = lngArg ?? Number(form?.longitude);
        if (!Number.isFinite(latVal) || !Number.isFinite(lngVal)) {
            setMessage("Укажите координаты.");
            return;
        }
        setDetectingPlace(true);
        setPlaceInfo(null);
        try {
            const { data } = await axios.post(`${API_BASE_URL}/api/place/info`, {
                lat: latVal,
                lng: lngVal,
            });
            setPlaceInfo(data);
            setField("maxDepth", data?.maxDepth != null ? String(data.maxDepth) : "");
            const allowed = new Set(((data?.fish || []).map((f) => Number(f.id))).filter(Number.isFinite));
            if (allowed.size > 0) {
                setSelectedFishIds((prev) => prev.filter((id) => allowed.has(id)));
            }
            setMessage("");
        } catch {
            setPlaceInfo(null);
            setMessage("Не удалось определить водоём.");
        } finally {
            setDetectingPlace(false);
        }
    }, [form?.latitude, form?.longitude, setField]);

    const pickCoordinates = useCallback((rawLat, rawLng) => {
        const clickLat = Number(Number(rawLat).toFixed(7));
        const clickLng = Number(Number(rawLng).toFixed(7));
        if (!Number.isFinite(clickLat) || !Number.isFinite(clickLng)) return;
        setField("latitude", String(clickLat));
        setField("longitude", String(clickLng));
        detectPlace(clickLat, clickLng);
    }, [setField, detectPlace]);

    function MapClickPicker() {
        useMapEvents({
            click(event) {
                pickCoordinates(event.latlng.lat, event.latlng.lng);
            },
        });
        return null;
    }

    function handlePhotoChange(type, fileList) {
        const result = validatePhotoSelection(type, fileList);
        setFiles((prev) => ({ ...prev, [type]: result.files }));
        setFileErrors((prev) => ({ ...prev, [type]: result.error }));
    }

    function runStepValidation() {
        const result = validateStep({
            mode,
            currentStep,
            form,
            files,
            fileErrors,
            drawPoints,
            lat,
            lng,
            placeInfo,
        });
        setFieldErrors((prev) => ({ ...prev, ...result.fieldErrors }));
        setFileErrors((prev) => ({ ...prev, ...result.fileErrors }));
        setRouteError(result.routeError);
        if (!result.isValid) {
            showFormFeedback(setMessage, setMessageType, "Проверьте поля формы.");
            return false;
        }
        setMessage("");
        setMessageType("error");
        return true;
    }

    const resetAfterSubmit = () => {
        setFiles({ place: [], catch: [], depth: [] });
        setFileErrors({});
        setFieldErrors({});
        setRouteError("");
        setDrawPoints([]);
        setDrawSegments([]);
        setRouteDescription("");
        setDrawMode("move");
        setCurrentStep(0);
        setMaxReachedStep(0);
        if (isCreate) {
            setForm(CREATE_INITIAL_FORM_STATE);
            setPlaceInfo(null);
            setSelectedFishIds([]);
            window.localStorage.removeItem(NEW_SPOT_DRAFT_STORAGE_KEY);
        }
    };

    const submit = async (event) => {
        event.preventDefault();
        if (currentStep !== stepTitles.length - 1 || !form) {
            return;
        }

        const validation = validateBeforeSubmit({
            mode,
            form,
            files,
            fileErrors,
            drawPoints,
            lat,
            lng,
            reportFormOpen,
            reportFileErrors,
        });
        setFieldErrors(validation.fieldErrors);
        setFileErrors(validation.fileErrors);
        setRouteError(validation.routeError);
        if (!validation.isValid) {
            showFormFeedback(setMessage, setMessageType, "Проверьте поля формы.");
            return;
        }

        setSaving(true);
        setMessage("");
        try {
            const values = isCreate
                ? { form, lat, lng, placeInfo, selectedFishIds }
                : { form, placeId: spot?.place?.id };
            const formData = buildSpotFormData(
                values,
                files,
                { drawPoints, routeDescription },
                mode,
                reportFormOpen,
                reportFiles,
            );

            if (isCreate) {
                await axios.post(`${API_BASE_URL}/api/spots`, formData, { withCredentials: true });
            } else {
                await axios.post(`${API_BASE_URL}/api/spots/${spotId}/draft`, formData, { withCredentials: true });
            }

            const successText = isCreate
                ? "После модерации ваша точка будет отображена на сайте."
                : "Правки отправлены на модерацию. Спасибо!";
            setMessage(successText);
            setMessageType("success");
            if (!isCreate) {
                showNotification({ type: "success", text: successText });
            }
            resetAfterSubmit();
            onSubmitSuccess?.();
        } catch (e) {
            const data = e?.response?.data || {};
            const errorText = data.error || data.message || (isCreate
                ? "Не удалось сохранить точку."
                : "Не удалось сохранить правки. Проверьте заполнение полей и попробуйте снова.");
            if (/маршрут|линии маршрута|координат/i.test(errorText)) {
                setRouteError(errorText);
            }
            setMessage(errorText);
            setMessageType("error");
            scrollPageToTop();
        } finally {
            setSaving(false);
        }
    };

    const goNext = () => {
        if (currentStep >= stepTitles.length - 1) return;
        if (!runStepValidation()) return;
        const nextStep = currentStep + 1;
        setCurrentStep(nextStep);
        setMaxReachedStep((prev) => Math.max(prev, nextStep));
    };

    const goBack = () => {
        if (currentStep === 0) return;
        setCurrentStep((prev) => prev - 1);
    };

    const undoRouteStroke = () => {
        setDrawSegments((prev) => {
            if (!prev.length) return prev;
            const removed = prev[prev.length - 1].length || 0;
            if (removed > 0) {
                setDrawPoints((points) => points.slice(0, -removed));
            }
            return prev.slice(0, -1);
        });
        setRouteError("");
    };

    const clearRoute = () => {
        setDrawPoints([]);
        setDrawSegments([]);
        setRouteError("");
    };

    const currentPhotoSections = isCreate ? [] : [
        { title: "Фото места", photos: spot?.placePhotos || [] },
        { title: "Фото улова", photos: spot?.catchPhotos || [] },
        { title: "Фото эхолота", photos: spot?.deepPhotos || [] },
    ];
    const currentPhotoCount = currentPhotoSections.flatMap((s) => s.photos || []).filter(Boolean).length;

    if (!form) {
        return null;
    }

    const formClassName = "page-grid-spot-data";

    return (
        <>
            {!!message && (
                <p className={messageType === "success" ? "form-message--success" : "form-message"}>
                    {message}
                </p>
            )}
            <WizardStepTabs
                stepTitles={stepTitles}
                currentStep={currentStep}
                maxReachedStep={maxReachedStep}
                onOpenStep={setCurrentStep}
                ariaLabel={isCreate ? "Этапы добавления точки" : "Этапы редактирования предложения"}
            />
            <form onSubmit={submit} className={formClassName} noValidate>
                {stepTitle === "Локация" && (
                    <section className="spot-form-panel spot-form-panel--wide spot-form-panel--no-box">
                        <div className="spot-form-header">
                            <h2>Местоположение на карте</h2>
                        </div>
                        <div className="spot-form-data">
                            <div style={{ gridColumn: "1 / -1" }}>
                                <p className="profile-tip" style={{ gridTemplateColumn: "none", width: "100%" }}>
                                    Подсказка: Нажмите на карту, чтобы выбрать координаты и определить ближайший водоём.
                                </p>
                                <MapContainer center={[53.885, 27.554]} zoom={7} zoomControl={false} style={{ height: 320, width: "100%", borderRadius: 16, overflow: "hidden" }} className="new-spot-map">
                                    <TileLayer attribution="&copy; OpenStreetMap contributors" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                    <MapZoomLocateControl onLocate={pickCoordinates} />
                                    <MapClickPicker />
                                    <MapDepthViewportListener onViewportChange={loadDepthPoints} />
                                    <SonarDepthLayer points={depthPoints} />
                                    {form.latitude && form.longitude && (
                                        <Marker icon={spotIcon} position={[Number(form.latitude), Number(form.longitude)]} />
                                    )}
                                </MapContainer>
                                {fieldError(fieldErrors.coordinates)}
                                {form.latitude && form.longitude && (
                                    <p style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: "10px 28px", alignItems: "baseline", fontSize: "18px", lineHeight: 1.45 }}>
                                        <span>Широта: <strong>{form.latitude}</strong></span>
                                        <span>Долгота: <strong>{form.longitude}</strong></span>
                                        <span>
                                            Водоём:{" "}
                                            <strong>
                                                {detectingPlace
                                                    ? "…"
                                                    : (placeInfo?.name || (placeInfo?.status === "not_found" ? "не найден" : "—"))}
                                            </strong>
                                        </span>
                                    </p>
                                )}
                            </div>
                        </div>
                    </section>
                )}

                {stepTitle === "Информация" && isCreate && (
                    <>
                        <section className="spot-form-panel spot-form-panel--wide spot-form-panel--no-box">
                            <div className="spot-form-header">
                                <h2>Описание</h2>
                            </div>
                            <div className="spot-form-data">
                                <label htmlFor="spot-title" style={{ gridColumn: "1 / -1" }}>
                                    Название точки (до 140 символов)<FormRequiredMark />
                                    <input id="spot-title" className={inputClass("title")} value={form.title} onChange={(e) => setField("title", e.target.value)} maxLength={140} required />
                                    {fieldError(fieldErrors.title)}
                                </label>
                                <label htmlFor="spot-description" style={{ gridColumn: "1 / -1" }}>
                                    Описание точки<FormRequiredMark />
                                    <textarea id="spot-description" className={inputClass("description", "ui-textarea")} value={form.description} onChange={(e) => setField("description", e.target.value)} required />
                                    {fieldError(fieldErrors.description)}
                                </label>
                            </div>
                        </section>
                        {renderGeneralInfoPanels(form, setField, inputClass, setShoreFishing, fieldErrors)}
                    </>
                )}

                {stepTitle === "Информация" && !isCreate && (
                    <>
                        <section className="spot-form-panel spot-form-panel--wide spot-form-panel--no-box">
                            <div className="spot-form-header">
                                <h2>Описание</h2>
                            </div>
                            <div className="spot-form-data">
                                <label htmlFor="edit-description" style={{ gridColumn: "1 / -1" }}>
                                    Описание точки<FormRequiredMark />
                                    <textarea id="edit-description" className={inputClass("description", "ui-textarea")} value={form.description} onChange={(e) => setField("description", e.target.value)} required />
                                    {fieldError(fieldErrors.description)}
                                </label>
                            </div>
                        </section>
                        {renderGeneralInfoPanels(form, setField, inputClass, setShoreFishing, fieldErrors)}
                    </>
                )}

                {stepTitle === "Рыба" && (
                    isCreate ? (
                        <section className="spot-form-panel spot-form-panel--wide spot-form-panel--no-box">
                            <div className="spot-form-header">
                                <h2>Что ловится в этой точке</h2>
                            </div>
                            <div className="spot-form-data">
                                <div style={{ gridColumn: "1 / -1" }}>
                                    {renderFishList(orderedFish, selectedFishIds, setSelectedFishIds, fishSearch, setFishSearch, isCreate, form, setForm)}
                                </div>
                            </div>
                        </section>
                    ) : (
                        <div style={{ gridColumn: "1 / -1" }}>
                            {renderFishList(orderedFish, selectedFishIds, setSelectedFishIds, fishSearch, setFishSearch, isCreate, form, setForm)}
                        </div>
                    )
                )}

                {stepTitle === "Фото" && renderPhotosStep({
                    isCreate,
                    files,
                    fileErrors,
                    handlePhotoChange,
                    setFiles,
                    currentPhotoCount,
                    currentPhotoSections,
                    setPreviewPhoto,
                })}

                {stepTitle === "Маршрут" && renderRouteStep({
                    isCreate,
                    lat,
                    lng,
                    drawPoints,
                    drawMode,
                    setDrawMode,
                    routeError,
                    routeDescription,
                    setRouteDescription,
                    depthPoints,
                    undoRouteStroke,
                    clearRoute,
                    setDrawSegments,
                    setDrawPoints,
                    setRouteError,
                    existingRoutes,
                })}

                {isCreate && stepTitle === "Отчёт" && renderReportStep({
                    form,
                    reportFormOpen,
                    setReportFormOpen,
                    setField,
                    fieldErrors,
                    handleReportPhotoChange,
                    reportFiles,
                    setReportFiles,
                    reportFileErrors
                })}

                <WizardNavButtons
                    currentStep={currentStep}
                    totalSteps={stepTitles.length}
                    saving={saving}
                    onBack={goBack}
                    onNext={goNext}
                    submitLabel={isCreate ? "Сохранить точку" : "Отправить на модерацию"}
                    savingLabel={isCreate ? "Сохраняем..." : "Отправка..."}
                />
            </form>
            {!isCreate && !!message && <p style={{ marginTop: 12 }}>{message}</p>}
            {previewPhoto && (
                <div
                    role="button"
                    tabIndex={-1}
                    onClick={() => setPreviewPhoto(null)}
                    onKeyDown={() => {}}
                    style={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 4000,
                        background: "rgba(15, 23, 42, 0.7)",
                        display: "grid",
                        placeItems: "center",
                        padding: 16,
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            width: "min(1100px, 96vw)",
                            maxHeight: "92vh",
                            background: "#fff",
                            borderRadius: 14,
                            padding: 12,
                            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.35)",
                        }}
                    >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                            <strong>{previewPhoto.title}</strong>
                            <button type="button" className="ghost-btn" onClick={() => setPreviewPhoto(null)}>Закрыть</button>
                        </div>
                        <img
                            src={previewPhoto.src}
                            alt={previewPhoto.title}
                            style={{ width: "100%", maxHeight: "calc(92vh - 100px)", objectFit: "contain", display: "block" }}
                        />
                    </div>
                </div>
            )}
        </>
    );
}

function renderGeneralInfoPanels(form, setField, inputClass, setShoreFishing, fieldErrors) {
    return (
        <>
            <section className="spot-form-panel spot-form-panel--no-box">
                <div className="spot-form-header">
                    <h2>Основная информация</h2>
                </div>
                <div className="spot-form-data">
                    <div style={{ gridColumn: "1 / -1" }} className="new-spot-info-grid">
                        <label>
                            Ловля с берега?
                            <div className="spot-segmented">
                                <button type="button" className={form.fishingFromShore ? "is-active" : ""} onClick={() => setShoreFishing(true)}>Да</button>
                                <button type="button" className={!form.fishingFromShore ? "is-active" : ""} onClick={() => setShoreFishing(false)}>Нет</button>
                            </div>
                        </label>
                        <label htmlFor="spot-capacity">
                            Количество мест на точке<FormRequiredMark />
                            <input id="spot-capacity" className={inputClass("capacityPersons")} type="number" min="1" max="20" step="1" value={form.capacityPersons} onChange={(e) => setField("capacityPersons", e.target.value)} />
                            {fieldError(fieldErrors.capacityPersons)}
                        </label>
                        <label htmlFor="spot-max-depth">
                            Максимальная глубина (м)<FormRequiredMark />
                            <input id="spot-max-depth" className={inputClass("maxDepth")} type="number" min="0" max="100" step="0.1" value={form.maxDepth} onChange={(e) => setField("maxDepth", e.target.value)} />
                            {fieldError(fieldErrors.maxDepth)}
                        </label>
                        <label htmlFor="spot-road">
                            Дорога<FormRequiredMark />
                            <select id="spot-road" className="ui-input" value={form.roadType} onChange={(e) => setField("roadType", e.target.value)} required>
                                {ROAD_TYPE_OPTIONS.map((option) => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </select>
                        </label>
                        <label htmlFor="spot-bank">
                            Берег<FormRequiredMark />
                            <select id="spot-bank" className={inputClass("bankType")} value={form.bankType} onChange={(e) => setField("bankType", e.target.value)} required>
                                <option value="" disabled>Выберите тип берега</option>
                                {BANK_TYPE_OPTIONS.map((option) => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </select>
                            {fieldError(fieldErrors.bankType)}
                        </label>
                        <label htmlFor="spot-current-strength">
                            Сила течения
                            <select id="spot-current-strength" className="ui-input" value={form.currentStrength} onChange={(e) => setField("currentStrength", e.target.value)}>
                                {CURRENT_STRENGTH_OPTIONS.map((option) => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </select>
                        </label>
                    </div>
                </div>
            </section>
            <section className="spot-form-panel spot-form-panel--no-box spot-form-panel--addl">
                <div className="spot-form-header">
                    <h2>Дополнительная информация</h2>
                </div>
                <div className="spot-form-data">
                    <label><input type="checkbox" checked={form.isWinterHole} onChange={(e) => setField("isWinterHole", e.target.checked)} />&nbsp;Зимовальная яма</label>
                    <label><input type="checkbox" checked={form.isSpawningArea} onChange={(e) => setField("isSpawningArea", e.target.checked)} />&nbsp;Нерестовая зона</label>
                    <label><input type="checkbox" checked={form.tentAllowed} onChange={(e) => setField("tentAllowed", e.target.checked)} />&nbsp;Есть место для палатки</label>
                    <label><input type="checkbox" checked={form.nightFishingAllowed} onChange={(e) => setField("nightFishingAllowed", e.target.checked)} />&nbsp;Место подходит для ночной рыбалки</label>
                    <label><input type="checkbox" checked={form.suitableForChildren} onChange={(e) => setField("suitableForChildren", e.target.checked)} />&nbsp;Место подходит для детей</label>
                    <label><input type="checkbox" checked={form.hasParking} onChange={(e) => setField("hasParking", e.target.checked)} />&nbsp;Есть парковка в доступности</label>
                    <label><input type="checkbox" checked={form.hasShelter} onChange={(e) => setField("hasShelter", e.target.checked)} />&nbsp;Есть возможность укрыться (навес, беседка)</label>
                </div>
            </section>
        </>
    );
}

function renderFishList(orderedFish, selectedFishIds, setSelectedFishIds, fishSearch, setFishSearch, isCreate, form, setForm) {
    return (
        <>
            {!isCreate && (
                <label htmlFor="spot-fish-list" style={{ display: "block", marginBottom: 8 }}>
                    Что ловится в этой точке
                </label>
            )}
            <input
                type="text"
                className="ui-input"
                value={fishSearch}
                onChange={(e) => setFishSearch(e.target.value)}
                placeholder="Введите название рыбы"
                style={{ maxWidth: 420, marginBottom: 10 }}
            />
            <div id="spot-fish-list" className="new-spot-fish-list">
                {orderedFish.map((f) => {
                    const fishId = Number(f.id);
                    const checked = isCreate
                        ? selectedFishIds.includes(fishId)
                        : form.fishIds.has(fishId);
                    return (
                        <label key={f.id} style={{ display: "flex", gap: 8, alignItems: "center", cursor: "pointer" }}>
                            <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                    if (isCreate) {
                                        if (e.target.checked) {
                                            setSelectedFishIds((prev) => (prev.includes(fishId) ? prev : [...prev, fishId]));
                                        } else {
                                            setSelectedFishIds((prev) => prev.filter((id) => id !== fishId));
                                        }
                                        return;
                                    }
                                    const next = new Set(form.fishIds);
                                    if (e.target.checked) {
                                        next.add(fishId);
                                    } else {
                                        next.delete(fishId);
                                    }
                                    setForm((prev) => (prev ? { ...prev, fishIds: next } : prev));
                                }}
                            />
                            <span>{f.name}</span>
                        </label>
                    );
                })}
            </div>
        </>
    );
}

function renderExistingPhotosSection({ currentPhotoSections, currentPhotoCount, setPreviewPhoto }) {
    if (currentPhotoCount <= 0) {
        return null;
    }

    return (
        <section className="spot-form-panel spot-form-panel--wide spot-form-panel--no-box new-spot-existing-photos-panel">
            <div className="spot-form-header">
                <h2>Текущие фото ({currentPhotoCount})</h2>
            </div>
            <div className="spot-form-data">
                {currentPhotoSections.map((section) => {
                    const sectionPhotos = (section.photos || []).filter(Boolean);
                    if (!sectionPhotos.length) return null;
                    return (
                        <div key={section.title} className="new-spot-existing-photos__section">
                            <h3 className="new-spot-existing-photos__title">{section.title}</h3>
                            <div className="new-spot-existing-photos__grid">
                                {sectionPhotos.map((photo, idx) => {
                                    const src = normalizePhotoSrc(photo);
                                    if (!src) return null;
                                    return (
                                        <button
                                            key={`${section.title}-${idx}`}
                                            type="button"
                                            className="new-spot-existing-photos__thumb"
                                            onClick={() => setPreviewPhoto({ src, title: section.title })}
                                            aria-label={`Открыть фото ${idx + 1} (${section.title})`}
                                        >
                                            <img
                                                src={src}
                                                alt={`${section.title} ${idx + 1}`}
                                            />
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}

function renderPhotoUploadSection({ title, type, tip, files, fileErrors, handlePhotoChange, setFiles }) {
    return (
        <section className="spot-form-panel spot-form-panel--wide spot-form-panel--no-box" key={type}>
            <div className="spot-form-header">
                <h2>{title}</h2>
            </div>
            <div className="spot-form-data">
                <div>
                    <div style={{ marginBottom: "4px", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                        {tip && <p className="profile-tip" style={{ gridTemplateColumn: "none", width: "100%" }}>{tip}</p>}
                        <label className="btn-outline" style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "8px" }}>
                            <span>{title}</span>
                            <input
                                type="file"
                                multiple
                                accept={PHOTO_UPLOAD_ACCEPT}
                                onChange={(e) => {
                                    handlePhotoChange(type, e.target.files);
                                    e.target.value = "";
                                }}
                                style={{ display: "none" }}
                            />
                        </label>
                        {fieldError(fileErrors[type])}
                    </div>
                    <PhotoUploadPreview
                        files={files[type]}
                        onRemove={(index) => setFiles((v) => ({ ...v, [type]: v[type].filter((_, i) => i !== index) }))}
                    />
                </div>
            </div>
        </section>
    );
}

function renderPhotosStep({ isCreate, files, fileErrors, handlePhotoChange, setFiles, currentPhotoCount, currentPhotoSections, setPreviewPhoto }) {
    const tip = "Подсказка: загрузите до 5 файлов, формата JPG/PNG, до 10 МБ каждый";
    const uploadLabels = [
        { title: "Фото места", type: "place" },
        { title: "Фото улова", type: "catch" },
        { title: "Фото эхолота", type: "depth" },
    ];

    if (isCreate) {
        return (
            <div className="spot-photos-grid" style={{ gridColumn: "1 / -1" }}>
                {uploadLabels.map(({ title, type }) => renderPhotoUploadSection({ title, type, tip, files, fileErrors, handlePhotoChange, setFiles }))}
            </div>
        );
    }

    return (
        <>
            <p className="profile-tip" style={{ gridColumn: "1 / -1" }}>
                Новые фото будут отправлены на модерацию вместе с остальными правками.
            </p>
            <div className="spot-photos-grid" style={{ gridColumn: "1 / -1" }}>
                {uploadLabels.map(({ title, type }) => renderPhotoUploadSection({ title, type, tip, files, fileErrors, handlePhotoChange, setFiles }))}
            </div>
            {renderExistingPhotosSection({ currentPhotoSections, currentPhotoCount, setPreviewPhoto })}
        </>
    );
}

function renderExistingRoutesSection({ existingRoutes }) {
    if (!existingRoutes.length) {
        return null;
    }

    return (
        <section className="spot-form-panel spot-form-panel--wide spot-form-panel--no-box new-spot-existing-routes-panel">
            <div className="spot-form-header">
                <h2>Текущие маршруты ({existingRoutes.length})</h2>
            </div>
            <div className="spot-form-data">
                <div className="new-spot-existing-routes__list">
                    {existingRoutes.map((route, index) => (
                        <div key={route.id || `existing-route-${index}`} className="new-spot-existing-routes__item">
                            Маршрут {index + 1}
                            {route.description ? `: ${route.description}` : ""}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function renderRouteStep({
    isCreate,
    lat,
    lng,
    drawPoints,
    drawMode,
    setDrawMode,
    routeError,
    routeDescription,
    setRouteDescription,
    depthPoints,
    undoRouteStroke,
    clearRoute,
    setDrawSegments,
    setDrawPoints,
    setRouteError,
    existingRoutes,
}) {
    const mapCenter = isCreate
        ? [Number.isFinite(lat) ? lat : 53.885, Number.isFinite(lng) ? lng : 27.554]
        : [lat, lng];
    const mapZoom = isCreate
        ? (Number.isFinite(lat) && Number.isFinite(lng) ? 13 : 8)
        : 13;
    const drawPointsLabel = drawPoints.length > 0 ? ` (${drawPoints.length} точек)` : "";

    return (
        <>
            {!isCreate && (
                <p className="profile-tip" style={{ gridColumn: "1 / -1" }}>
                    Новый маршрут будет отправлен на модерацию вместе с остальными правками.
                </p>
            )}
            {!isCreate && (
                <div style={{ gridColumn: "1 / -1" }}>
                    {renderExistingRoutesSection({ existingRoutes })}
                </div>
            )}
            <section className="spot-form-panel spot-form-panel--wide spot-form-panel--no-box new-spot-route-panel" style={{ gridColumn: "1 / -1" }}>
                <div className="spot-form-header">
                    <h2>Нарисовать маршрут{drawPointsLabel}</h2>
                </div>
                <div className="spot-form-data">
                    <div className="new-spot-route-spoiler">
                        {isCreate && (
                            <p className="profile-tip" style={{ gridTemplateColumn: "none", width: "100%" }}>
                                Ваша точка будет отправлена как черновик и появится на карте после модерации.
                            </p>
                        )}
                        <p className="new-spot-route-hint">
                            Маршрут необязателен. Если рисуете его, начало или конец должны быть не дальше 100 м от точки, все точки маршрута — в пределах 10 км.
                        </p>
                        {routeError && <p className="form-message">{routeError}</p>}
                        <div className="draw-toolbar-row">
                            <div className="draw-mode-toggle">
                                <span>Перемещение</span>
                                <button
                                    type="button"
                                    className={`draw-mode-toggle__switch ${drawMode === "draw" ? "is-draw" : ""}`}
                                    onClick={() => setDrawMode((prev) => (prev === "draw" ? "move" : "draw"))}
                                    aria-label={drawMode === "draw" ? "Выключить рисование" : "Включить рисование"}
                                >
                                    <span className="draw-mode-toggle__thumb" />
                                </button>
                                <span>Рисование</span>
                            </div>
                            <div className="draw-toolbar-row__actions">
                                <button
                                    type="button"
                                    className="btn-outline"
                                    style={{ padding: "8px" }}
                                    title="Назад"
                                    aria-label="Назад"
                                    onClick={undoRouteStroke}
                                >
                                    Отменить
                                </button>
                                <button
                                    type="button"
                                    className="btn-outline"
                                    style={{ padding: "8px" }}
                                    title="Очистить"
                                    aria-label="Очистить"
                                    onClick={clearRoute}
                                >
                                    Очистить
                                </button>
                            </div>
                        </div>
                        <MapContainer
                            center={mapCenter}
                            zoom={mapZoom}
                            zoomControl={false}
                            style={{ height: 360, width: "100%", borderRadius: 16, overflow: "hidden" }}
                            className="new-spot-map"
                        >
                            <TileLayer attribution="&copy; OpenStreetMap contributors" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                            <MapZoomLocateControl />
                            <InvalidateMapSizeOnMount />
                            {isCreate && <SonarDepthLayer points={depthPoints} />}
                            {Number.isFinite(lat) && Number.isFinite(lng) && (
                                <Marker icon={spotIcon} position={[lat, lng]} />
                            )}
                            {!isCreate && existingRoutes.map((route, idx) => {
                                const line = routeToLine(route);
                                if (line.length < 2) return null;
                                return (
                                    <Polyline
                                        key={route.id || `existing-route-${idx}`}
                                        positions={line}
                                        pathOptions={{ color: "#64748b", weight: 3, opacity: 0.8 }}
                                    />
                                );
                            })}
                            <Polyline positions={drawPoints} />
                            <DrawMapInteraction
                                mode={drawMode}
                                onStrokeStart={() => setDrawSegments((prev) => [...prev, []])}
                                onAdd={(point) => {
                                    setDrawPoints((prev) => [...prev, point]);
                                    setDrawSegments((prev) => {
                                        if (!prev.length) return [[point]];
                                        const copy = [...prev];
                                        copy[copy.length - 1] = [...copy[copy.length - 1], point];
                                        return copy;
                                    });
                                    setRouteError("");
                                }}
                            />
                        </MapContainer>
                        <textarea
                            className="ui-textarea"
                            rows={2}
                            value={routeDescription}
                            onChange={(e) => setRouteDescription(e.target.value)}
                            placeholder="Описание маршрута (необязательно)"
                        />
                    </div>
                </div>
            </section>
        </>
    );
}

function renderReportStep({form, reportFormOpen, setReportFormOpen, setField, fieldErrors, handleReportPhotoChange, reportFiles, setReportFiles, reportFileErrors}) {
    const tip = "Подсказка: Информация о вашей рыбалке не является обязательной, но Вы можете добавить её на этой вкладке.";
    const reportInputClass = (name, baseClass = "ui-input") => `${baseClass}${reportFileErrors[name] ? " is-invalid" : ""}`;
    const maxFishingDateTime = toDateTimeLocalValue(new Date());

    return (
        <section className="spot-form-panel spot-form-panel--wide spot-form-panel--no-box">
            <div className="spot-form-header">
                <h2>Отчёт</h2>
            </div>
            <div className="spot-form-data">
                <div>
                    <div style={{ marginBottom: "4px", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                        {tip && <p className="profile-tip" style={{ gridTemplateColumn: "none", width: "100%" }}>{tip}</p>}
                    </div>
                    {!reportFormOpen && (
                        <button
                            type="button"
                            className="btn-outline"
                            style={{ marginTop: 8, marginBottom: 12 }}
                            onClick={() => setReportFormOpen(true)}
                        >
                            Добавить Отчёт
                        </button>
                    )}
                    {reportFormOpen && (
                        <button
                            type="button"
                            className="btn-outline"
                            style={{ marginTop: 8, marginBottom: 12, color: "#ef4444", borderColor: "rgba(239,68,68,0.35)" }}
                            onClick={() => setReportFormOpen(false)}
                        >
                            Удалить Отчёт
                        </button>
                    )}
                    {reportFormOpen && (
                        <>
                            <div className="page-grid">
                                <label>
                                    Начало рыбалки<FormRequiredMark />
                                    <input
                                        className={reportInputClass("startTime")}
                                        type="datetime-local"
                                        max={maxFishingDateTime}
                                        value={form.startTime}
                                        required
                                        onChange={(e) => setField("startTime", e.target.value)}
                                    />
                                    {fieldError(fieldErrors.startTime)}
                                </label>

                                <label>
                                    Конец рыбалки<FormRequiredMark />
                                    <input
                                        className={reportInputClass("endTime")}
                                        type="datetime-local"
                                        max={maxFishingDateTime}
                                        value={form.endTime}
                                        required
                                        onChange={(e) => setField("endTime", e.target.value)}
                                    />
                                    {fieldError(fieldErrors.endTime)}
                                </label>

                                <label>
                                    Что поймали
                                    <input
                                        className="ui-input"
                                        value={form.whatCaught}
                                        onChange={(e) => setField("whatCaught", e.target.value)}
                                    />
                                    {fieldError(fieldErrors.whatCaught)}
                                </label>

                                <label>Наживка
                                    <input
                                        className="ui-input"
                                        value={form.baitUsed}
                                        onChange={(e) => setField("baitUsed", e.target.value)}
                                    />
                                    {fieldError(fieldErrors.baitUsed)}
                                </label>

                                <label>
                                    Прикормка
                                    <input
                                        className="ui-input"
                                        value={form.groundbait}
                                        onChange={(e) => setField("groundbait", e.target.value)}
                                    />
                                    {fieldError(fieldErrors.groundbait)}
                                </label>

                                <label>
                                    Глубина<FormRequiredMark />
                                    <input
                                        className={reportInputClass("depth")}
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="0.1"
                                        value={form.depth}
                                        required
                                        onChange={(e) => setField("depth", e.target.value)}
                                    />
                                    {fieldError(fieldErrors.depth)}
                                </label>

                                <label>
                                    Тип ловли<FormRequiredMark />
                                    <OptionMultiSelect
                                        options={fishingTypeSelectOptions()}
                                        value={form.fishingType}
                                        onChange={(selected) => setField("fishingType", selected)}
                                        invalid={!!fieldErrors.fishingType}
                                        aria-label="Тип ловли"
                                    />
                                    {fieldError(fieldErrors.fishingType)}
                                </label>
                                <div style={{ gridColumn: "1 / -1" }}>
                                    <p style={{ margin: "4px 0 0", fontWeight: 600, fontSize: "0.95rem" }}>Отчёт<FormRequiredMark /></p>
                                    <textarea
                                        className={reportInputClass("report", "ui-textarea")}
                                        rows={2}
                                        required
                                        value={form.report}
                                        onChange={(e) => setField("report", e.target.value)}
                                    />
                                    {fieldError(fieldErrors.report)}
                                    <p style={{ margin: "10px 0 10px", fontWeight: 600, fontSize: "0.95rem" }}>Фото</p>
                                    <div style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "12px",
                                        flexWrap: "wrap",
                                        marginBottom: "8px"
                                    }}>
                                        <label
                                            className="btn-outline"
                                            style={{
                                                cursor: "pointer",
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: "8px"
                                        }}>
                                            <span>Фото улова</span>
                                            <input
                                                type="file"
                                                multiple
                                                accept={PHOTO_UPLOAD_ACCEPT}
                                                onChange={(e) => {
                                                    handleReportPhotoChange("catch", e.target.files);
                                                    e.target.value = "";
                                                }}
                                                style={{ display: "none" }}
                                            />
                                        </label>
                                    </div>
                                    <div style={{ 
                                        fontSize: "0.85rem", 
                                        color: "var(--text-muted)", 
                                        marginBottom: "12px"
                                    }}>
                                        До 5 файлов, JPG/PNG, до 10 МБ каждый
                                    </div>
                                    {(reportFileErrors.catch) && (
                                        <div>
                                            {fieldError(reportFileErrors.catch)}
                                        </div>
                                    )}
                                </div>

                            </div>
                            <PhotoUploadPreview
                                files={reportFiles.catch}
                                onRemove={(index) => setReportFiles((v) => ({ ...v, catch: v.catch.filter((_, i) => i !== index) }))}
                            />
                        </>
                    )}
                </div>
            </div>
        </section>
    );
}
